import React from 'react'

export const faq = [
  {
    name: 'Getting Started',
    questions: [
      {
        q: `Can we run Shivoo by ourselves?`,
        a: <>
          <p>Yes — and that’s one of the big advantages.</p>
          <p>You can run Shivoo:</p>
          <ul>
            <li>With your team, in your regular meeting</li>
            <li>Without a facilitator</li>
            <li>Or with a coach if you want deeper support</li>
          </ul>
          <p>It’s designed to work either way.</p>
        </>
      },
      {
        q: `Do I need to be accredited to run Shivoo?`,
        a: <>
          <p>No.</p>
          <p>Shivoo is designed to be easy to use straight away.</p>
          <p>You can:</p>
          <ul>
            <li>Jump in and run your first squiz immediately</li>
            <li>Or review guides and support if you prefer</li>
          </ul>
          <p>Most teams are up and running within minutes.</p>
        </>
      },
      {
        q: `How long does a Shivoo session take?`,
        a: <>
          <p>Most squizzes take 30 – 60 minutes.</p>
          <p>Short enough to fit into a regular team meeting — long enough to generate meaningful insight and
            discussion.</p>
        </>
      },
      {
        q: `Do we need to prepare anything in advance?`,
        a: <>
          <p>No.</p>
          <p>There’s no pre - work, no surveys, and no setup burden</p>
          <p>Just bring your team — and start.</p>
        </>
      }
    ]
  },
  {
    name: 'Using Shivoo',
    questions: [
      {
        q: `What is a "Squiz"?`,
        a: <>
          <p>A “squiz” is what at we call each time you run Shivoo with your team.</p>
          <p>It’s simply:</p>
          <ul>
            <li>A structured moment to “have a look” at how your team is working.</li>
          </ul>
          <p>Yes, it’s an Australian term. And yes, you can add “mate” if you like.</p>
        </>
      },
      {
        q: `What makes Shivoo different from other team tools?`,
        a: <>
          <p>Most tools:</p>
          <ul>
            <li>Survey individuals</li>
            <li>Deliver reports later</li>
          </ul>
          <p>Shivoo:</p>
          <ul>
            <li>Works in real time</li>
            <li>Focuses on the team (not individuals)</li>
            <li>Combines measurement, conversation, and development</li>
          </ul>
          <p>It doesn’t just measure your team — it helps you improve it.</p>
        </>
      },
      {
        q: `What happens if our team disagrees on answers?`,
        a: <>
          <p>That’s where the value is.</p>
          <p>Disagreement:</p>
          <ul>
            <li>Surfaces different perspectives</li>
            <li>Triggers important conversations</li>
            <li>Helps build alignment</li>
          </ul>
          <p>Shivoo is designed to use disagreement — not avoid it.</p>
        </>
      },
      {
        q: `What kind of teams is Shivoo for?`,
        a: <>
          <p>Shivoo works for any team that works together regularly.</p>
          <p>Including:</p>
          <ul>
            <li>Leadership teams</li>
            <li>Functional teams</li>
            <li>Project teams</li>
          </ul>
          <p>If you collaborate, Shivoo applies.</p>
        </>
      },
      {
        q: `How often should we run Shivoo?`,
        a: <>
          <p>Most teams run a squiz every 6 – 12 weeks.</p>
          <p>That gives enough time to:</p>
          <ul>
            <li>Act on insights</li>
            <li>Build new habits</li>
            <li>See measurable progress</li>
          </ul>
        </>
      },
    ]
  },
  {
    name: 'Pricing & Scaling',
    questions: [
      {
        q: `How does pricing work?`,
        a: <>
          <p>Shivoo is a simple annual subscription.</p>
          <p>While your subscription is active, you can:</p>
          <ul>
            <li>Run as many Squizzes as you like.</li>
            <li>Access your results anytime</li>
            <li>Track progress over time</li>
            <li>Compare against our global database</li>
            <li>Use our tips, tools, and development guidance</li>
          </ul>
          <p>No limits. No per-use charges. Just ongoing access.</p>
        </>
      },
      {
        q: `We have multiple teams in our organization, can we link them together?`,
        a: <>
          <p>Yes — Shivoo scales across teams.</p>
          <p>With Enterprise Shivoo, you can:</p>
          <ul>
            <li>Manage multiple teams in one place</li>
            <li>Compare performance across your organisation</li>
            <li>Access organisation - level insights</li>
          </ul>
          <p>You can start with as few as two teams and grow from there.</p>
        </>
      },
    ]
  },
  {
    name: 'Data & Credibility',
    questions: [
      {
        q: `What data do you capture and hold?`,
        a: <>
          <p>We measure the team — not individuals.</p>
          <p>That means:</p>
          <ul>
            <li>We store team - level answers and result</li>
            <li>We do <b>not</b> track individual responses</li>
            <li>We do <b>not</b> store personal or identifiable participant data</li>
          </ul>
          <p>We use your team’s data to:</p>
          <ul>
            <li>Show your results</li>
            <li>Enable comparisons with other teams</li>
            <li>We do <b>not</b> store personal or identifiable participant data</li>
          </ul>
          <p>Your team gets insight — without individual exposure.</p>
        </>
      },
      {
        q: `Is this a credible tool? Does it have a robust basis?`,
        a: <>
          <p>Yes — Shivoo is built on rigorous research and real - world application.</p>
          <p>It draws on:</p>
          <ul>
            <li>40+ years of field experience</li>
            <li>10+ years of academic research</li>
            <li>A PhD in complex team dynamics</li>
          </ul>
          <p>It is informed by leading thinkers in complexity and performance, including:</p>
          <ul>
            <li>Stacey</li>
            <li>Snowden (Cynefin)</li>
            <li>Arrow</li>
            <li>Ericsson</li>
          </ul>
          <p>In short: it’s academically grounded, but built to be practical.</p>
        </>
      },
      {
        q: `If I cancel my subscription, do I lose access to my data?`,
        a: <>
          <p>Yes — once your subscription ends, you lose access to your team’s results.</p>
          <p>Your de-identified data remains part of the overall dataset (for benchmarking), but:</p>
          <ul>
            <li>It is no longer accessible to you</li>
            <li>It cannot be recovered later</li>
          </ul>
          <p>This approach protects privacy and keeps the system clean.</p>
        </>
      },
    ]
  },
]